import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Datagrid from '../ui/Datagrid';
import { gridClasses } from '@mui/x-data-grid';
import columns from './table_config';
import { CircularProgress, Grid, IconButton, TablePagination, Typography } from '@mui/material';
import { renderCell } from '../ui/Datagrid/table_config';
import PropTypes from 'prop-types';
import { REDUX_ACTIONS } from '../../shared/config/constants';
import { useGridApiRef } from '@mui/x-data-grid-pro';
import { downloadUserBugAttachments, listUserBugs } from '../../store/actions/userBugs';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import Tooltip from '@mui/material/Tooltip';
import { USER_BUG_PROGRESS_LEVEL, USER_TYPE } from '../../utils/constants';
import { useAuth } from '../../hooks/useAuth';
import BugReportUpdateDialog from '../ui/BugReportUpdateDialog';

// eslint-disable-next-line react/prop-types
function CustomPagination({ currentPage, limit, totalCount, handlePagination, loading }) {
  return (
    <TablePagination
      component="div"
      count={totalCount}
      page={currentPage}
      onPageChange={(event, page) => !loading && handlePagination(page)}
      rowsPerPage={limit}
      rowsPerPageOptions={[]}
    />
  );
}

const UserBugs = () => {
  const { user } = useAuth();
  const userBugs = useSelector((state) => state.userBugs);
  const dispatch = useDispatch();
  const [filter, setFilter] = useState([]);
  const [bugReportProgressUpdate, setBugReportProgressUpdate] = useState({
    data: {},
    open: false
  });

  const apiRef = useGridApiRef();

  useEffect(() => {
    if (!userBugs.loading && userBugs.data.length === 0) {
      dispatch(listUserBugs());
    }
  }, []);

  useEffect(() => {
    if (filter.length === 0 && userBugs.data.length) {
      dispatch(listUserBugs());
    }
  }, [filter]);

  const handlePagination = (page) => {
    if (page < userBugs.currentPage) {
      dispatch(listUserBugs({ page: userBugs.currentPage - 1 }));
      dispatch({
        type: REDUX_ACTIONS.USER_BUGS_SET_CURRENT_PAGE,
        payload: userBugs.currentPage - 1
      });
    } else {
      dispatch(listUserBugs({ page: userBugs.currentPage + 1 }));
      dispatch({
        type: REDUX_ACTIONS.USER_BUGS_SET_CURRENT_PAGE,
        payload: page + 1
      });
    }
    apiRef.current.setFilterModel({
      items: filter
    });
    apiRef.current.scrollToIndexes({
      rowIndex: 0,
      colIndex: 0,
    });
  };

  const renderProgressField = ({ row }) => {
    return (
      <Typography sx={{
        fontSize: 'medium',
        fontWeight: 'bold !important'
      }}>{USER_BUG_PROGRESS_LEVEL[row?.progress]}</Typography>
    );
  };

  const renderAttachmentField = ({ row }) => {
    const [downloading, setDownloading] = useState(false);
    if (!row?.attachments?.length) return null;
    return (
      <IconButton onClick={() => {
        setDownloading(true);
        dispatch(downloadUserBugAttachments(row?._id, row?.issue, setDownloading));
      }}>
        {downloading ? <CircularProgress size="20px" /> : <FileDownloadIcon />}
      </IconButton>
    );
  };

  const renderLocationField = ({ row }) => {
    const location = row?.location;
    if (location?.length > 1) {
      return (
        <Typography sx={{
          display: 'flex',
          fontSize: 'medium',
          fontWeight: 'bold !important'
        }}>
          {location[0]}
        <Tooltip
          title={<React.Fragment>
            <Typography color="inherit">{location?.slice(1)?.join(', ')}</Typography>
          </React.Fragment>}
        ><Typography sx={{
          ml: 0.5,
          fontSize: 'small',
          fontWeight: 'bold !important',
          color: '#666'
        }}>+ {location?.length - 1} More</Typography>
        </Tooltip>
        </Typography>
      );
    }
    return (
      <Typography sx={{
        fontSize: 'medium',
        fontWeight: 'bold !important'
      }}>{location[0]}</Typography>
    );
  };

  function findKeyOfProgress (obj, value) {
    for (const key of Object.keys(obj)) {
      if (obj[key] === value) {
        return key;
      }
    }
    return null;
  }

  return (
    <Grid container justifyContent="center">
      <Grid item xs={12}>
        <Datagrid
          apiRef={apiRef}
          data={userBugs?.data}
          columns={columns(renderCell, renderLocationField, renderAttachmentField, renderProgressField)}
          loading={userBugs.loading}
          disableStickyHeader={false}
          height={'90vh'}
          getRowId={(row) => row.grid_id}
          autoPageSize
          autoHeight={false}
          pagination={false}
          hideFooter
          experimentalFeatures={{ newEditingApi: true }}
          filterMode="server"
          getRowHeight={() => 'auto'}
          onCellDoubleClick={({ row, field }) => {
            if (field === 'progress' && user.type === USER_TYPE.PREMIUM_ADMIN) {
              setBugReportProgressUpdate({
                data: row,
                open: true
              });
            }
          }}
          sx={{
            '.MuiDataGrid-columnHeaderTitle': {
              fontWeight: 'bold !important',
            },
            '.MuiDataGrid-cell': {
              fontSize: 'medium',
              fontWeight: 'bold !important',
              wordBreak: 'break-word'
            },
            [`& .${gridClasses.cell}`]: {
              py: 1,
            },
          }}
          toolbar={{
            options: {
              columns: true,
              filters: true,
              export: {
                enabled: true,
                options: true,
              },
            },
          }}
          onFilterModelChange={ (model) => {
            setFilter(model.items);
            const items = model.items.map(filter => {
              if (filter.columnField === 'progress') {
                const progressKey = findKeyOfProgress(USER_BUG_PROGRESS_LEVEL, filter.value);
                return { ...filter, value: progressKey, columnField: 'details.' + filter.columnField };
              }
              return { ...filter, columnField: 'details.' + filter.columnField };
            });
            const filterModels = { ...model, items };
            dispatch({
              type: REDUX_ACTIONS.USER_BUGS_SET_FILTER,
              payload: filterModels.items.length ? filterModels : {}
            });
          }}
          sortingMode="server"
          onSortModelChange={(sortModel) => {
            const sort = sortModel.reduce((result, item) => {
              result['details.' + item.field] = item.sort;
              return result;
            }, {});
            dispatch({
              type: REDUX_ACTIONS.USER_BUGS_SET_SORT,
              payload: sort
            });
            dispatch({
              type: REDUX_ACTIONS.USER_BUGS_SET_CURRENT_PAGE,
              payload: 1
            });
            dispatch(listUserBugs());
          }}
        />
        <CustomPagination
          currentPage={userBugs.currentPage - 1}
          limit={userBugs.limit}
          totalCount={userBugs.totalCount}
          handlePagination={handlePagination}
          loading={userBugs.loading}
        />
      </Grid>
      {bugReportProgressUpdate?.open && (
        <BugReportUpdateDialog
        isOpen={bugReportProgressUpdate.open}
        data={bugReportProgressUpdate?.data}
        onCloseClick={() => {
          setBugReportProgressUpdate({
            data: {},
            open: false
          });
        }}
        />
      )}
    </Grid>
  );
};

UserBugs.propTypes = {
  row: PropTypes.object,
};

export default UserBugs;
